import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/api/client';
import type { Vacancy } from '@/types';

interface HHAccount {
  id: string;
  name: string;
  company_name?: string | null;
  hh_employer_id?: string | null;
  status: string; // pending | connected
  last_sync_at?: string | null;
}

type VacancyWithHH = Vacancy & { hh_account_id?: string };

export default function HHRU() {
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState<HHAccount[]>([]);
  const [vacancies, setVacancies] = useState<VacancyWithHH[]>([]);
  const [connecting, setConnecting] = useState<string | null>(null);
  const [syncing, setSyncing] = useState<string | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: '', company_name: '', hh_employer_id: '', hh_client_id: '', hh_client_secret: '' });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const [acc, vacs] = await Promise.all([
      api.get<HHAccount[]>('/hh/accounts').catch(() => []),
      api.get<VacancyWithHH[]>('/vacancies').catch(() => []),
    ]);
    setAccounts(acc);
    setVacancies(vacs);
  }, []);

  useEffect(() => { load().finally(() => setLoading(false)); }, [load]);

  const vacanciesOf = (accountId: string) =>
    vacancies.filter((v) => v.hh_account_id === accountId && v.hh_vacancy_id);

  const handleConnect = async (id: string) => {
    setConnecting(id);
    try {
      const { auth_url } = await api.post<{ auth_url: string }>(`/hh/accounts/${id}/auth`, {});
      window.open(auth_url, '_blank');
      alert('Авторизуйтесь на hh.ru в открывшейся вкладке, затем вернитесь и обновите страницу.');
    } catch (err) {
      alert((err as Error).message || 'Ошибка подключения');
    }
    setConnecting(null);
  };

  const handleSync = async (account: HHAccount) => {
    const linked = vacanciesOf(account.id);
    if (linked.length === 0) {
      alert('К аккаунту не привязано ни одной вакансии с hh.ru');
      return;
    }
    setSyncing(account.id);
    let total = 0;
    try {
      for (const v of linked) {
        const r = await api.post<{ imported: number }>(`/vacancies/${v.id}/candidates/import-hh`, {});
        total += r.imported || 0;
      }
      await load();
      alert(total > 0 ? `Импортировано новых откликов: ${total}` : 'Новых откликов нет');
    } catch (err) {
      alert((err as Error).message || 'Ошибка синхронизации');
    }
    setSyncing(null);
  };

  const handleAdd = async () => {
    if (!form.name.trim() || !form.hh_client_id.trim() || !form.hh_client_secret.trim()) {
      alert('Заполните название, Client ID и Client Secret (из dev.hh.ru)');
      return;
    }
    setSaving(true);
    try {
      await api.post('/hh/accounts', form);
      setShowAdd(false);
      setForm({ name: '', company_name: '', hh_employer_id: '', hh_client_id: '', hh_client_secret: '' });
      await load();
    } catch (err) {
      alert((err as Error).message || 'Ошибка создания');
    }
    setSaving(false);
  };

  const formatDate = (iso?: string | null) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="w-8 h-8 border-2 border-primary-300 border-t-primary-500 rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl md:text-2xl font-heading font-bold text-foreground-950">
            hh.ru — интеграция
          </h1>
          <p className="text-sm text-foreground-500 mt-1">
            Подключённые аккаунты работодателя, импорт откликов и публикация вакансий
          </p>
        </div>
        <button
          onClick={() => setShowAdd(true)}
          className="text-xs flex items-center gap-1.5 px-3 py-2.5 rounded-xl bg-primary-500 text-white hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-add-line" />
          Добавить аккаунт
        </button>
      </div>

      {/* Accounts */}
      <div className="space-y-4">
        {accounts.length === 0 && (
          <div className="bg-white rounded-xl border border-background-200 p-8 text-center text-sm text-foreground-500">
            Аккаунтов hh.ru пока нет. Добавьте аккаунт с Client ID/Secret приложения из dev.hh.ru.
          </div>
        )}
        {accounts.map((account) => {
          const linked = vacanciesOf(account.id);
          return (
            <div key={account.id} className="bg-white rounded-xl border border-background-200 p-5 md:p-6">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center shrink-0">
                    <i className="ri-briefcase-line text-blue-600 text-lg" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="text-base font-semibold text-foreground-900">{account.name}</h3>
                      {account.status === 'connected' ? (
                        <span className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 flex items-center gap-1">
                          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                          Подключён
                        </span>
                      ) : (
                        <span className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-background-100 text-foreground-500">
                          Не подключён
                        </span>
                      )}
                    </div>
                    <div className="flex flex-wrap items-center gap-3 mt-1 text-xs text-foreground-500">
                      {account.hh_employer_id && (
                        <span className="flex items-center gap-1">
                          <i className="ri-hashtag" />
                          employer {account.hh_employer_id}
                        </span>
                      )}
                      <span className="flex items-center gap-1">
                        <i className="ri-history-line" />
                        Последняя синхронизация: {formatDate(account.last_sync_at)}
                      </span>
                      <span className="flex items-center gap-1">
                        <i className="ri-file-list-line" />
                        {linked.length} вакансий привязано
                      </span>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  {account.status === 'connected' ? (
                    <button
                      onClick={() => handleSync(account)}
                      disabled={syncing === account.id}
                      className="text-xs flex items-center gap-1.5 px-3 py-2 rounded-lg bg-background-100 text-foreground-600 hover:bg-background-200 transition-colors disabled:opacity-50 cursor-pointer whitespace-nowrap"
                    >
                      {syncing === account.id ? (
                        <i className="ri-loader-4-line animate-spin" />
                      ) : (
                        <i className="ri-refresh-line" />
                      )}
                      Импортировать отклики
                    </button>
                  ) : (
                    <button
                      onClick={() => handleConnect(account.id)}
                      disabled={connecting === account.id}
                      className="text-xs flex items-center gap-1.5 px-3 py-2 rounded-lg bg-accent-500 text-white hover:bg-accent-600 transition-colors disabled:opacity-50 cursor-pointer whitespace-nowrap"
                    >
                      {connecting === account.id ? (
                        <i className="ri-loader-4-line animate-spin" />
                      ) : (
                        <i className="ri-link" />
                      )}
                      Подключить (OAuth)
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Quick info */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border border-background-200 p-5">
          <div className="w-10 h-10 rounded-xl bg-purple-50 flex items-center justify-center mb-3">
            <i className="ri-download-cloud-line text-purple-600 text-lg" />
          </div>
          <h3 className="text-sm font-semibold text-foreground-900 mb-1">Автоимпорт откликов</h3>
          <p className="text-xs text-foreground-500">Новые отклики импортируются, оцениваются AI и получают приглашение на анкету автоматически — каждые 20 минут</p>
        </div>

        <div className="bg-white rounded-xl border border-background-200 p-5">
          <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center mb-3">
            <i className="ri-upload-cloud-line text-amber-600 text-lg" />
          </div>
          <h3 className="text-sm font-semibold text-foreground-900 mb-1">Публикация вакансий</h3>
          <p className="text-xs text-foreground-500 mb-3">Публикуйте вакансии на hh.ru из карточки вакансии</p>
          <button onClick={() => navigate('/vacancies')} className="text-xs text-amber-700 font-medium hover:text-amber-800 transition-colors cursor-pointer">
            К вакансиям →
          </button>
        </div>

        <div className="bg-white rounded-xl border border-background-200 p-5">
          <div className="w-10 h-10 rounded-xl bg-primary-50 flex items-center justify-center mb-3">
            <i className="ri-chat-3-line text-primary-600 text-lg" />
          </div>
          <h3 className="text-sm font-semibold text-foreground-900 mb-1">Двусторонний чат</h3>
          <p className="text-xs text-foreground-500 mb-3">Отвечайте кандидатам в чате hh.ru прямо из карточки кандидата</p>
          <button onClick={() => navigate('/candidates')} className="text-xs text-primary-700 font-medium hover:text-primary-800 transition-colors cursor-pointer">
            Открыть кандидатов →
          </button>
        </div>
      </div>

      {/* Add account modal */}
      {showAdd && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={() => setShowAdd(false)}>
          <div className="bg-white rounded-2xl w-full max-w-md shadow-xl p-6 space-y-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-lg font-heading font-bold text-foreground-950">Новый аккаунт hh.ru</h2>
            <p className="text-xs text-foreground-400">Создайте приложение на dev.hh.ru и укажите его ключи. После сохранения нажмите «Подключить» для OAuth-авторизации.</p>
            {([
              ['name', 'Название (для себя)'],
              ['company_name', 'Компания'],
              ['hh_employer_id', 'ID работодателя на hh'],
              ['hh_client_id', 'Client ID'],
              ['hh_client_secret', 'Client Secret'],
            ] as const).map(([key, label]) => (
              <div key={key}>
                <label className="text-xs font-medium text-foreground-600 mb-1 block">{label}</label>
                <input
                  value={form[key]}
                  onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                  className="w-full px-3 py-2.5 rounded-xl bg-background-50 border border-background-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400"
                />
              </div>
            ))}
            <div className="flex gap-2 pt-2">
              <button onClick={() => setShowAdd(false)} className="flex-1 py-2.5 rounded-xl border border-background-200 text-sm text-foreground-600 hover:bg-background-50 cursor-pointer">
                Отмена
              </button>
              <button onClick={handleAdd} disabled={saving} className="flex-1 py-2.5 rounded-xl bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 disabled:opacity-50 cursor-pointer">
                {saving ? 'Сохраняю...' : 'Сохранить'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
